import { useCallback } from 'react';

import {
  transactionConfettiConfig,
  TransactionConfettiTriggerConfig,
} from '@/constants/transactionConfettiConfig';

import { tokenKey } from '@/utils/normalizeAddress';
import { TokenInfo } from '@/utils/token.types';

import { useConfetti, ConfettiDisplayConfig } from './useConfetti';

/** Transaction fields needed to resolve and dedupe transaction success confetti. */
export type TransactionConfettiContext = {
  txHash?: string;
  sellToken: TokenInfo;
  buyToken: TokenInfo;
};

const firedTransactionHashes = new Set<string>();

const tokenMatches = (context: TransactionConfettiContext, tokens: TokenInfo[]) => {
  const { buyToken, sellToken } = context;
  const tokenKeys = new Set(tokens.map(({ address, chainId }) => tokenKey(address, chainId)));

  return (
    tokenKeys.has(tokenKey(sellToken.address, sellToken.chainId)) ||
    tokenKeys.has(tokenKey(buyToken.address, buyToken.chainId))
  );
};

const triggerMatches = (
  trigger: TransactionConfettiTriggerConfig,
  context: TransactionConfettiContext,
) => {
  const { buyToken, sellToken } = context;
  const { chains, sameChainOnly, tokens } = trigger.match;
  const isSameChainTrade = sellToken.chainId === buyToken.chainId;

  if (sameChainOnly && !isSameChainTrade) return false;
  if (chains && !chains.includes(sellToken.chainId) && !chains.includes(buyToken.chainId)) {
    return false;
  }
  if (tokens && !tokenMatches(context, tokens)) return false;

  return true;
};

export const resolveTransactionConfettiConfig = (
  context: TransactionConfettiContext,
): ConfettiDisplayConfig | undefined => {
  const customTrigger = transactionConfettiConfig.triggers.find(
    (trigger) => trigger.enabled && triggerMatches(trigger, context),
  );

  if (customTrigger) return customTrigger.confetti;
  if (transactionConfettiConfig.default) return { variant: 'default' };

  return undefined;
};

export const useTransactionConfetti = () => {
  const { triggerConfetti } = useConfetti();

  const triggerTransactionConfetti = useCallback(
    async (context: TransactionConfettiContext) => {
      const { txHash } = context;
      if (!txHash || firedTransactionHashes.has(txHash)) return;

      const displayConfig = resolveTransactionConfettiConfig(context);
      if (!displayConfig) return;

      firedTransactionHashes.add(txHash);
      await triggerConfetti(displayConfig);
    },
    [triggerConfetti],
  );

  return { triggerTransactionConfetti };
};
